import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  Link,
  ListItemText,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { Alert, Skeleton } from '@material-ui/lab';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import {
  generateInterviewReport,
  generateInterviewQuestions,
} from 'lib/Api/InterviewReportGenerator';
import ReactMarkdown from 'react-markdown';
import { getUserData } from 'lib/Helper/Auth';

const useStyles = makeStyles((theme) => ({
  formControl: {
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(1),
  },
  cardContent: {
    paddingTop: 0,
  },
  input: {
    display: 'none',
  },
  fileName: {
    marginLeft: theme.spacing(1),
    fontStyle: 'italic',
  },
  uploadButton: {
    marginRight: theme.spacing(1),
  },
  reportContainer: {
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    maxHeight: '500px',
    overflowY: 'auto',
    '& p': { marginBottom: theme.spacing(2) },
  },
  modeSelector: {
    marginBottom: theme.spacing(2),
  },
  stickyActions: {
    position: 'sticky',
    bottom: 0,
    backgroundColor: theme.palette.background.paper,
    borderTop: `1px solid ${theme.palette.divider}`,
    zIndex: 1,
    padding: theme.spacing(1, 2),
  },
  toneSelect: {
    marginBottom: theme.spacing(2),
  },
  toneMenuItem: {
    whiteSpace: 'normal',
  },
  toneRenderValue: {
    display: 'flex',
    flexDirection: 'column',
    lineHeight: 1.4,
  },
  toneRenderLabel: {
    fontWeight: 500,
  },
  toneRenderDescription: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
  },
  contextSection: {
    backgroundColor: theme.palette.grey[50],
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  contextHeader: {
    marginBottom: theme.spacing(1),
  },
  contextSubtext: {
    marginBottom: theme.spacing(2),
  },
}));

const TONE_OPTIONS = [
  {
    value: 'balanced',
    label: 'Balanced',
    description: 'Professional and structured with moderate detail.',
  },
  {
    value: 'detailed',
    label: 'Detailed & Narrative',
    description:
      "Fuller narrative that paints a picture of the candidate's career journey.",
  },
  {
    value: 'direct',
    label: 'Direct & Impactful',
    description:
      'Shorter, punchier sentences that lead with achievements and results.',
  },
] as const;

const QUESTION_STYLE_OPTIONS = [
  {
    value: 'balanced',
    label: 'Balanced Mix',
    description:
      'A blend of behavioural, competency and experience-based questions.',
  },
  {
    value: 'behavioural',
    label: 'Behavioural-Focused',
    description:
      'Lean into STAR-style prompts that probe past behaviour, motivation and soft skills.',
  },
  {
    value: 'technical',
    label: 'Technical / Competency Deep-Dive',
    description:
      'Prioritise role-specific technical and competency-based questions.',
  },
] as const;

const FieldLabel = ({ text, tip }: { text: string; tip: string }) => (
  <Box
    display="flex"
    alignItems="center"
    style={{ gap: 4, pointerEvents: 'auto' }}
  >
    {text}
    <Tooltip title={tip} arrow placement="top">
      <InfoOutlinedIcon
        style={{ fontSize: 15, cursor: 'pointer', opacity: 0.5 }}
      />
    </Tooltip>
  </Box>
);

/**
 * Captures a snapshot of all form inputs for dirty-state comparison.
 * Used to determine whether the Regenerate button should appear.
 */
function getInputSnapshot(fields: {
  selectedFileName: string | null;
  geminiNotesFileName: string | null;
  careerMotivation: string;
  signatureAchievement: string;
  behaviouralAnecdote: string;
  logistics: string;
  recruiterAssessment: string;
  otherInformation: string;
  specificFocus: string;
  tone: string;
  interviewingForVacancy: boolean;
  jobDescription: string;
  questionStyle: string;
  questionsFocus: string;
}): string {
  return JSON.stringify(fields);
}

const InterviewReportGeneratorView: React.FC = () => {
  const classes = useStyles();
  const user = getUserData();
  const reportRef = useRef<HTMLDivElement>(null);
  const reportSectionRef = useRef<HTMLDivElement>(null);

  const hasAccess = user?.permissions?.includes('interview_report_generator');

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [geminiNotesFile, setGeminiNotesFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [mode, setMode] = useState<'questions' | 'report'>('questions');
  const [isInfoDialogOpen, setIsInfoDialogOpen] = useState(false);
  const [isCvOnlyDialogOpen, setIsCvOnlyDialogOpen] = useState(false);

  const [careerMotivation, setCareerMotivation] = useState<string>('');
  const [signatureAchievement, setSignatureAchievement] = useState<string>('');
  const [behaviouralAnecdote, setBehaviouralAnecdote] = useState<string>('');
  const [logistics, setLogistics] = useState<string>('');
  const [recruiterAssessment, setRecruiterAssessment] = useState<string>('');
  const [otherInformation, setOtherInformation] = useState<string>('');
  const [specificFocus, setSpecificFocus] = useState<string>('');
  const [tone, setTone] = useState<'balanced' | 'detailed' | 'direct'>(
    'balanced',
  );

  // Questions-mode tailoring
  const [interviewingForVacancy, setInterviewingForVacancy] =
    useState<boolean>(false);
  const [jobDescription, setJobDescription] = useState<string>('');
  const [questionStyle, setQuestionStyle] = useState<
    'balanced' | 'behavioural' | 'technical'
  >('balanced');
  const [questionsFocus, setQuestionsFocus] = useState<string>('');

  // Tracks the input state at the time of last successful generation
  const lastGeneratedSnapshot = useRef<string | null>(null);

  const currentSnapshot = getInputSnapshot({
    selectedFileName: selectedFile?.name ?? null,
    geminiNotesFileName: geminiNotesFile?.name ?? null,
    careerMotivation,
    signatureAchievement,
    behaviouralAnecdote,
    logistics,
    recruiterAssessment,
    otherInformation,
    specificFocus,
    tone,
    interviewingForVacancy,
    jobDescription,
    questionStyle,
    questionsFocus,
  });

  const hasInputChanged =
    lastGeneratedSnapshot.current !== null &&
    lastGeneratedSnapshot.current !== currentSnapshot;

  const hasAnyOptionalContent =
    !!geminiNotesFile ||
    !!careerMotivation ||
    !!signatureAchievement ||
    !!behaviouralAnecdote ||
    !!logistics ||
    !!recruiterAssessment ||
    !!otherInformation ||
    !!specificFocus;

  useEffect(() => {
    if (mode !== 'report') {
      setGeminiNotesFile(null);
      const geminiInput = document.getElementById(
        'gemini-notes-upload',
      ) as HTMLInputElement;
      if (geminiInput) geminiInput.value = '';
    }
  }, [mode]);

  if (!hasAccess) {
    return (
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Alert severity="error">
            You do not have permission to access this feature.
          </Alert>
        </Grid>
      </Grid>
    );
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      setSelectedFile(event.target.files[0]);
    } else {
      setSelectedFile(null);
    }
  };

  const handleClearFields = () => {
    setCareerMotivation('');
    setSignatureAchievement('');
    setBehaviouralAnecdote('');
    setLogistics('');
    setRecruiterAssessment('');
    setOtherInformation('');
    setSpecificFocus('');
    setTone('balanced');
    setInterviewingForVacancy(false);
    setJobDescription('');
    setQuestionStyle('balanced');
    setQuestionsFocus('');
    setReport(null);
    setSelectedFile(null);
    setGeminiNotesFile(null);
    lastGeneratedSnapshot.current = null;
    const resumeInput = document.getElementById(
      'cv-upload',
    ) as HTMLInputElement;
    if (resumeInput) resumeInput.value = '';
    const geminiInput = document.getElementById(
      'gemini-notes-upload',
    ) as HTMLInputElement;
    if (geminiInput) geminiInput.value = '';
  };

  const handleGeminiNotesFileChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (event.target.files && event.target.files.length > 0) {
      setGeminiNotesFile(event.target.files[0]);
    } else {
      setGeminiNotesFile(null);
    }
  };

  const doGenerate = async () => {
    if (!selectedFile) return;

    setLoading(true);
    setError(null);

    try {
      if (mode === 'report') {
        const response = await generateInterviewReport({
          resume: selectedFile,
          geminiNotesFile: geminiNotesFile || undefined,
          careerMotivation: careerMotivation || undefined,
          signatureAchievement: signatureAchievement || undefined,
          behaviouralAnecdote: behaviouralAnecdote || undefined,
          logistics: logistics || undefined,
          recruiterAssessment: recruiterAssessment || undefined,
          otherInformation: otherInformation || undefined,
          specificFocus: specificFocus || undefined,
          tone,
        });
        setReport(response.data);
      } else {
        const response = await generateInterviewQuestions({
          resume: selectedFile,
          interviewingForVacancy: interviewingForVacancy || undefined,
          jobDescription:
            interviewingForVacancy && jobDescription
              ? jobDescription
              : undefined,
          questionStyle,
          specificFocus: questionsFocus || undefined,
        });
        setReport(response.data);
      }

      // Capture snapshot at generation time
      lastGeneratedSnapshot.current = getInputSnapshot({
        selectedFileName: selectedFile?.name ?? null,
        geminiNotesFileName: geminiNotesFile?.name ?? null,
        careerMotivation,
        signatureAchievement,
        behaviouralAnecdote,
        logistics,
        recruiterAssessment,
        otherInformation,
        specificFocus,
        tone,
        interviewingForVacancy,
        jobDescription,
        questionStyle,
        questionsFocus,
      });

      setSnackbarOpen(true);

      setTimeout(() => {
        reportSectionRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }, 100);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Unknown error';
      setError(
        `Whoops! An error occurred while generating the ${
          mode === 'report' ? 'report' : 'questions'
        }: ${errorMsg}. You can try again later, but if the issue persists, please reach out to insights@konnekttalexio.com or through Google Chat so we can solve the issue!`,
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedFile) return;

    if (mode === 'report' && !hasAnyOptionalContent) {
      setIsCvOnlyDialogOpen(true);
      return;
    }

    doGenerate();
  };

  const handleConfirmCvOnly = () => {
    setIsCvOnlyDialogOpen(false);
    doGenerate();
  };

  const isGenerateDisabled = () => {
    if (loading) return true;
    if (!selectedFile) return true;
    if (
      mode === 'questions' &&
      interviewingForVacancy &&
      !jobDescription.trim()
    ) {
      return true;
    }
    return false;
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const handleModeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setMode(event.target.value as 'questions' | 'report');
    setReport(null);
    lastGeneratedSnapshot.current = null;
  };

  const selectedTone = TONE_OPTIONS.find((o) => o.value === tone);
  const selectedQuestionStyle = QUESTION_STYLE_OPTIONS.find(
    (o) => o.value === questionStyle,
  );

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h6">Interview Generator</Typography>
      </Grid>

      <Grid item xs={12} md={8} lg={6}>
        <Card>
          <CardHeader
            title={
              mode === 'report'
                ? 'Generate Interview Report'
                : 'Generate Interview Questions'
            }
            subheader={
              mode === 'report'
                ? "Upload the candidate's CV, optionally add interview context, and generate a client-ready report."
                : 'Upload a CV to generate tailored interview questions.'
            }
          />
          <CardContent className={classes.cardContent}>
            {/* Mode selector */}
            <FormControl component="fieldset" className={classes.modeSelector}>
              <RadioGroup
                row
                aria-label="generation-mode"
                name="generation-mode"
                value={mode}
                onChange={handleModeChange}
              >
                <FormControlLabel
                  value="questions"
                  control={<Radio color="primary" />}
                  label="Generate Questions"
                />
                <FormControlLabel
                  value="report"
                  control={<Radio color="primary" />}
                  label="Generate Report"
                />
              </RadioGroup>
            </FormControl>

            {/* CV upload */}
            <FormControl className={classes.formControl} fullWidth>
              <Typography variant="subtitle1" gutterBottom>
                Candidate CV
              </Typography>
              <Box display="flex" alignItems="center">
                <input
                  accept=".pdf,.txt,.docx"
                  className={classes.input}
                  id="cv-upload"
                  type="file"
                  onChange={handleFileChange}
                />
                <label htmlFor="cv-upload">
                  <Button
                    variant="contained"
                    color="default"
                    component="span"
                    startIcon={<CloudUploadIcon />}
                    className={classes.uploadButton}
                  >
                    Upload CV
                  </Button>
                </label>
                {selectedFile && (
                  <Typography variant="body2" className={classes.fileName}>
                    {selectedFile.name}
                  </Typography>
                )}
              </Box>
            </FormControl>

            {/* Questions-mode tailoring */}
            {mode === 'questions' && (
              <>
                <Divider style={{ marginBottom: 16 }} />

                <Box className={classes.contextSection}>
                  <Typography
                    variant="subtitle1"
                    className={classes.contextHeader}
                  >
                    Tailor the Questions (optional)
                  </Typography>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    className={classes.contextSubtext}
                  >
                    Add a job description and a few preferences to get questions
                    that are sharper and more relevant to the role.
                  </Typography>

                  <FormControl className={classes.formControl} fullWidth>
                    <FormControlLabel
                      control={
                        <Checkbox
                          color="primary"
                          checked={interviewingForVacancy}
                          onChange={(e) =>
                            setInterviewingForVacancy(e.target.checked)
                          }
                        />
                      }
                      label="Candidate is interviewing for a specific vacancy"
                    />
                  </FormControl>

                  {interviewingForVacancy && (
                    <FormControl className={classes.formControl} fullWidth>
                      <TextField
                        label={
                          <FieldLabel
                            text="Job Description"
                            tip="Paste the JD or a summary of the role. Questions will be tailored to the responsibilities, skills and seniority described."
                          />
                        }
                        multiline
                        rows={6}
                        variant="outlined"
                        InputLabelProps={{ shrink: true }}
                        value={jobDescription}
                        onChange={(e) => setJobDescription(e.target.value)}
                        placeholder="Paste the full job description, or a brief summary covering responsibilities, must-have skills, team context and seniority."
                        fullWidth
                      />
                    </FormControl>
                  )}

                  <FormControl
                    fullWidth
                    variant="outlined"
                    className={classes.formControl}
                  >
                    <Typography variant="subtitle2" gutterBottom>
                      Question Style
                    </Typography>
                    <Select
                      value={questionStyle}
                      onChange={(e) =>
                        setQuestionStyle(
                          e.target.value as
                            | 'balanced'
                            | 'behavioural'
                            | 'technical',
                        )
                      }
                      renderValue={() =>
                        selectedQuestionStyle ? (
                          <div className={classes.toneRenderValue}>
                            <span className={classes.toneRenderLabel}>
                              {selectedQuestionStyle.label}
                            </span>
                            <span className={classes.toneRenderDescription}>
                              {selectedQuestionStyle.description}
                            </span>
                          </div>
                        ) : null
                      }
                    >
                      {QUESTION_STYLE_OPTIONS.map((option) => (
                        <MenuItem
                          key={option.value}
                          value={option.value}
                          className={classes.toneMenuItem}
                        >
                          <ListItemText
                            primary={option.label}
                            secondary={option.description}
                          />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl
                    className={classes.formControl}
                    fullWidth
                    style={{ marginBottom: 0 }}
                  >
                    <TextField
                      label={
                        <FieldLabel
                          text="Specific Focus"
                          tip="Areas, skills or concerns you want the questions to dig into."
                        />
                      }
                      multiline
                      rows={2}
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      value={questionsFocus}
                      onChange={(e) => setQuestionsFocus(e.target.value)}
                      placeholder="E.g. probe stakeholder management, validate hands-on SQL experience, explore reasons for short tenures"
                      fullWidth
                    />
                  </FormControl>
                </Box>
              </>
            )}

            {/* Tone selector */}
            {mode === 'report' && (
              <FormControl
                fullWidth
                variant="outlined"
                className={classes.toneSelect}
              >
                <Typography variant="subtitle1" gutterBottom>
                  Report Style
                </Typography>
                <Select
                  value={tone}
                  onChange={(e) =>
                    setTone(
                      e.target.value as 'balanced' | 'detailed' | 'direct',
                    )
                  }
                  renderValue={() =>
                    selectedTone ? (
                      <div className={classes.toneRenderValue}>
                        <span className={classes.toneRenderLabel}>
                          {selectedTone.label}
                        </span>
                        <span className={classes.toneRenderDescription}>
                          {selectedTone.description}
                        </span>
                      </div>
                    ) : null
                  }
                >
                  {TONE_OPTIONS.map((option) => (
                    <MenuItem
                      key={option.value}
                      value={option.value}
                      className={classes.toneMenuItem}
                    >
                      <ListItemText
                        primary={option.label}
                        secondary={option.description}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Interview context section */}
            {mode === 'report' && (
              <>
                <Divider style={{ marginBottom: 16 }} />

                <Box className={classes.contextSection}>
                  <Typography
                    variant="subtitle1"
                    className={classes.contextHeader}
                  >
                    Interview Context
                  </Typography>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    className={classes.contextSubtext}
                  >
                    Adding interview notes produces a stronger report, but you
                    can generate from the CV alone. Notes can be as rough as you
                    like; the AI will do the rest.
                  </Typography>

                  {/* Gemini notes upload */}
                  <FormControl className={classes.formControl} fullWidth>
                    <Box display="flex" alignItems="center" mb={1}>
                      <Typography
                        variant="subtitle2"
                        style={{ marginRight: 8 }}
                      >
                        Gemini Meeting Notes
                      </Typography>
                      <Link
                        component="button"
                        variant="body2"
                        onClick={() => setIsInfoDialogOpen(true)}
                      >
                        (How do I get this?)
                      </Link>
                    </Box>
                    <Box display="flex" alignItems="center">
                      <input
                        accept=".pdf,.txt,.md"
                        className={classes.input}
                        id="gemini-notes-upload"
                        type="file"
                        onChange={handleGeminiNotesFileChange}
                      />
                      <label htmlFor="gemini-notes-upload">
                        <Button
                          variant="contained"
                          color="default"
                          component="span"
                          startIcon={<CloudUploadIcon />}
                          className={classes.uploadButton}
                          size="small"
                        >
                          Upload Notes
                        </Button>
                      </label>
                      {geminiNotesFile && (
                        <Typography
                          variant="body2"
                          className={classes.fileName}
                        >
                          {geminiNotesFile.name}
                        </Typography>
                      )}
                    </Box>
                  </FormControl>

                  {geminiNotesFile && (
                    <Box mb={2}>
                      <Alert severity="info">
                        The generator will primarily use the uploaded notes. The
                        fields below can provide additional context if needed.
                      </Alert>
                    </Box>
                  )}

                  <FormControl className={classes.formControl} fullWidth>
                    <TextField
                      label={
                        <FieldLabel
                          text="Career Motivation & Target Role"
                          tip="What's driving the candidate's job search and where do they want to go next?"
                        />
                      }
                      multiline
                      rows={2}
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      value={careerMotivation}
                      onChange={(e) => setCareerMotivation(e.target.value)}
                      placeholder="E.g. Targeting a Head of Ops role, wants to move away from pure AML into broader operations"
                      fullWidth
                    />
                  </FormControl>

                  <FormControl className={classes.formControl} fullWidth>
                    <TextField
                      label={
                        <FieldLabel
                          text="Signature Achievement"
                          tip="The accomplishments that best define this candidate professionally."
                        />
                      }
                      multiline
                      rows={2}
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      value={signatureAchievement}
                      onChange={(e) => setSignatureAchievement(e.target.value)}
                      placeholder="E.g. Grew user base from 10k to 100k in 6 months"
                      fullWidth
                    />
                  </FormControl>

                  <FormControl className={classes.formControl} fullWidth>
                    <TextField
                      label={
                        <FieldLabel
                          text="Behavioural Anecdote"
                          tip="A specific moment or behaviour that stood out - something not visible on the CV."
                        />
                      }
                      multiline
                      rows={2}
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      value={behaviouralAnecdote}
                      onChange={(e) => setBehaviouralAnecdote(e.target.value)}
                      placeholder="E.g. Proactively redesigned the onboarding process without being asked"
                      fullWidth
                    />
                  </FormControl>

                  <FormControl className={classes.formControl} fullWidth>
                    <TextField
                      label={
                        <FieldLabel
                          text="Logistics"
                          tip="Availability, notice period, salary expectations, location flexibility."
                        />
                      }
                      multiline
                      rows={2}
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      value={logistics}
                      onChange={(e) => setLogistics(e.target.value)}
                      placeholder="E.g. Available immediately, expecting €45k, open to hybrid"
                      fullWidth
                    />
                  </FormControl>

                  <FormControl className={classes.formControl} fullWidth>
                    <TextField
                      label={
                        <FieldLabel
                          text="Recruiter Assessment"
                          tip="Your honest take - strengths, red flags, overall fit."
                        />
                      }
                      multiline
                      rows={2}
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      value={recruiterAssessment}
                      onChange={(e) => setRecruiterAssessment(e.target.value)}
                      placeholder="E.g. Strong candidate, very confident, some concerns around communication style"
                      fullWidth
                    />
                  </FormControl>

                  <FormControl className={classes.formControl} fullWidth>
                    <TextField
                      label={
                        <FieldLabel
                          text="Other Information"
                          tip="Languages, work preferences, soft skills, personal context."
                        />
                      }
                      multiline
                      rows={2}
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      value={otherInformation}
                      onChange={(e) => setOtherInformation(e.target.value)}
                      placeholder="E.g. Strong English, native Maltese, prefers remote work"
                      fullWidth
                    />
                  </FormControl>

                  <FormControl
                    className={classes.formControl}
                    fullWidth
                    style={{ marginBottom: 0 }}
                  >
                    <TextField
                      label={
                        <FieldLabel
                          text="Specific Focus"
                          tip="Tell the AI what to emphasise in the report."
                        />
                      }
                      multiline
                      rows={2}
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      value={specificFocus}
                      onChange={(e) => setSpecificFocus(e.target.value)}
                      placeholder="E.g. Report should focus on candidate's people management skills, not AML background"
                      fullWidth
                    />
                  </FormControl>
                </Box>
              </>
            )}
          </CardContent>

          {/* Sticky action bar */}
          <CardActions className={classes.stickyActions}>
            <Button
              type="button"
              color="primary"
              variant="contained"
              onClick={handleSubmit}
              disabled={isGenerateDisabled()}
              startIcon={
                loading && <CircularProgress size={20} color="inherit" />
              }
            >
              {loading
                ? 'Generating...'
                : `Generate ${mode === 'report' ? 'Report' : 'Questions'}`}
            </Button>
            <Button
              type="button"
              color="secondary"
              variant="outlined"
              onClick={handleClearFields}
              disabled={loading}
              style={{ marginLeft: 8 }}
            >
              Clear All
            </Button>
          </CardActions>
        </Card>
      </Grid>

      <Grid item xs={12} md={8} lg={6} ref={reportSectionRef}>
        {loading && <Skeleton variant="rect" height={400} />}

        {!loading && error && <Alert severity="error">{error}</Alert>}

        {!loading && report && (
          <Card>
            <CardHeader
              title={`Generated Interview ${
                mode === 'report' ? 'Report' : 'Questions'
              }`}
            />
            <CardContent>
              <Typography className={classes.reportContainer} ref={reportRef}>
                <ReactMarkdown>{report}</ReactMarkdown>
              </Typography>
              <Typography
                variant="caption"
                display="block"
                style={{ marginTop: 8, fontStyle: 'italic' }}
              >
                Disclaimer: Content may contain inaccuracies. Please review
                thoroughly before presenting to candidates or clients.
              </Typography>
            </CardContent>
            <CardActions>
              <Button
                color="primary"
                onClick={async () => {
                  if (reportRef.current && report) {
                    const contentHtml = reportRef.current.innerHTML;
                    const blobHtmlData = new Blob([contentHtml], {
                      type: 'text/html',
                    });
                    const blobTextData = new Blob([report], {
                      type: 'text/plain',
                    });
                    try {
                      const clipItem = new (window as any).ClipboardItem({
                        'text/html': blobHtmlData,
                        'text/plain': blobTextData,
                      });
                      await (navigator.clipboard as any).write([clipItem]);
                      setSnackbarOpen(true);
                    } catch (err) {
                      navigator.clipboard
                        .writeText(report)
                        .then(() => {
                          setSnackbarOpen(true);
                        })
                        .catch(() => {
                          setError('Failed to copy to clipboard.');
                        });
                    }
                  }
                }}
              >
                Copy to Clipboard
              </Button>
              {hasInputChanged && (
                <Button
                  color="primary"
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={loading}
                  startIcon={
                    loading && <CircularProgress size={20} color="inherit" />
                  }
                >
                  Regenerate
                </Button>
              )}
            </CardActions>
          </Card>
        )}
      </Grid>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="success">
          {report && !loading
            ? 'Copied to clipboard!'
            : `${
                mode === 'report' ? 'Report' : 'Questions'
              } generated successfully!`}
        </Alert>
      </Snackbar>

      {/* CV-only confirmation dialog */}
      <Dialog
        open={isCvOnlyDialogOpen}
        onClose={() => setIsCvOnlyDialogOpen(false)}
      >
        <DialogTitle>Generate from CV only?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            The report will be based solely on the CV. Adding interview notes or
            filling in the context fields typically produces a stronger, more
            personalised result. Do you want to continue anyway?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsCvOnlyDialogOpen(false)}>Go Back</Button>
          <Button
            onClick={handleConfirmCvOnly}
            color="primary"
            variant="contained"
          >
            Generate Anyway
          </Button>
        </DialogActions>
      </Dialog>

      {/* Gemini notes info dialog */}
      <Dialog
        open={isInfoDialogOpen}
        onClose={() => setIsInfoDialogOpen(false)}
        aria-labelledby="gemini-info-dialog-title"
      >
        <DialogTitle id="gemini-info-dialog-title">
          How to Get Gemini Meeting Notes
        </DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            <ol>
              <li>
                If you used Gemini to transcribe an interview, you'll find the
                transcription and summary in "My Drive" and in the "Meet
                Recordings" folder. You'll also receive an email with the
                transcription and summary. If you're not transcribing your
                interviews yet, what are you waiting for? 😊
              </li>
              <li>Open the transcription file or summary in Google Docs.</li>
              <li>
                Click on "File" in the top menu.{' '}
                <strong>Download the file as a PDF.</strong>
              </li>
              <li>
                You can choose to download the summary or the entire
                transcription. The summary is usually more concise and relevant
                for the report.
              </li>
            </ol>
            <Alert severity="info">
              Uploading the full transcription may result in longer processing
              times for the report generation.
            </Alert>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsInfoDialogOpen(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default InterviewReportGeneratorView;
